import moment from "moment";
import { useCallback } from "react";
import { useNavigate } from "react-router";
import { v4 } from "uuid";
import { getToken } from "../auth/tokenStore";
import config from "../config";
import {
  Assessor,
  type CategoryScoringMap,
  Certificate,
  CertificateType,
  Company,
  DefaultInspectionAttachment,
  Exam,
  ExamParticipant,
  Project,
  ProposedChange,
  RealmRole,
  User,
  WhitelistedEmail,
} from "./dto";
import { CheckInCode } from "./dto/CheckInCode";
import { CheckInPrintCode } from "./dto/CheckInPrintCode";
import { CheckInVisitor } from "./dto/CheckInVisitor";
import { Checklist, type LiftingCategory } from "./dto/Checklist";
import { FlowCard, type TextContent } from "./dto/FlowCard";
import { Inspection } from "./dto/Inspection";
import { InspectionAttachment } from "./dto/InspectionAttachment";
import {
  type InspectionComment,
  InspectionFinding,
  type InspectionSeverity,
} from "./dto/InspectionFinding";
import { InspectionPhoto } from "./dto/InspectionPhoto";
import type { LoadCapacity } from "./dto/LoadCapacity";
import type { QuestionAnswerValue } from "./dto/Question";
import { QuestionSet } from "./dto/QuestionSet";
import { SharedInspectionFinding } from "./dto/SharedInspectionFinding";
import { UserPermissions, type UserPermissionsSettings } from "./dto/UserPermissions";
import { LiftingEquipment } from "./dto/LiftingEquipment";
import { MyCompanyPermissions } from "./dto/MyCompanyPermissions";
import { type CheckInPermissions, ProjectCheckIn } from "./dto/ProjectCheckIn";

const V1 = config.apiBaseUrlV1;
const V2 = config.apiBaseUrlV2;

const checkInBody = (permissions: CheckInPermissions, grantHours: number) => ({
  grant_hours: grantHours,
  permissions: {
    can_execute_assessments: permissions.canExecuteAssessments,
    can_export_assessments: permissions.canExportAssessments,
    can_access_all_assessments: permissions.canAccessAllAssessments,
    can_access_assessment_history: permissions.canAccessAssessmentHistory,
    can_manage_projects: permissions.canManageProjects,
    can_manage_assessments: permissions.canManageAssessments,
    can_manage_assessments_destructive: permissions.canManageAssessmentsDestructive,
    accessible_question_set_types: permissions.accessibleQuestionSetTypes,
  },
});

export const useApiCall = () => {
  const navigate = useNavigate();
  const checkForValidationErrors = async (response: Response): Promise<void> => {
    if (response.status === 401) {
      navigate("/expired");
      throw new ValidationError("Session expired", {});
    }
    if (response.status === 403) {
      navigate("/forbidden");
      throw new ValidationError("Insufficient permissions", {});
    }
    if (response.status === 422) {
      const json = await response.json();
      if ("message" in json && "errors" in json && typeof json.errors === "object") {
        throw new ValidationError(json.message, json.errors);
      }
      if ("message" in json) {
        throw new ValidationError(json.message, { message: [json.message] });
      }
      // v2 shape: {"error": {"field": ["message", ...]}}
      if ("error" in json && typeof json.error === "object" && json.error !== null) {
        const errors = json.error as { [key: string]: string[] };
        throw new ValidationError(Object.values(errors).flat().join(" "), errors);
      }
    }
  };

  const getRaw = useCallback(async (baseUrl: string, url: string) => {
    const response = await fetch(`${baseUrl}/${url}`, {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    });
    await checkForValidationErrors(response);
    return response;
  }, []);
  const get = useCallback(
    async <T>(baseUrl: string, url: string) => {
      const response = await getRaw(baseUrl, url);
      return (await response.json()) as { data: T };
    },
    [getRaw],
  );
  const post = useCallback(async <T>(baseUrl: string, url: string, body: object) => {
    const response = await fetch(`${baseUrl}/${url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    });
    await checkForValidationErrors(response);
    return (await response.json()) as { data: T };
  }, []);
  const postRawJson = useCallback(async <T>(baseUrl: string, url: string, body: object) => {
    const response = await fetch(`${baseUrl}/${url}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    });
    await checkForValidationErrors(response);
    return response;
  }, []);
  const postRawForm = useCallback(async <T>(baseUrl: string, url: string, body: FormData) => {
    const response = await fetch(`${baseUrl}/${url}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${getToken()}`,
      },
      body: body,
    });
    await checkForValidationErrors(response);
    return response;
  }, []);
  const postFile = useCallback(
    async <T>(baseUrl: string, url: string, name: string, file: File) => {
      const formData = new FormData();
      formData.append(name, file);
      const response = await fetch(`${baseUrl}/${url}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: formData,
      });
      await checkForValidationErrors(response);
      return response;
    },
    [],
  );
  const put = useCallback(async <T>(baseUrl: string, url: string, body: object) => {
    const response = await fetch(`${baseUrl}/${url}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify(body),
    });
    await checkForValidationErrors(response);
    return (await response.json()) as { data: T };
  }, []);
  const del = useCallback(async <T>(baseUrl: string, url: string) => {
    console.log("sending delete call");
    const response = await fetch(`${baseUrl}/${url}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
    });
    console.log("checking for validation errors");
    await checkForValidationErrors(response);
  }, []);

  return {
    // Check-ins (v2)
    // Without includeClosed only open check-ins are returned (which can still be past their expiry).
    async listProjectCheckIns(projectId: string, includeClosed = false) {
      const response = await get<object[]>(
        V2,
        `projects/${projectId}/check-ins${includeClosed ? "?include_closed=1" : ""}`,
      );
      return response.data.map((json) => ProjectCheckIn.fromJson(json));
    },
    async startProjectCheckIn(
      projectId: string,
      permissions: CheckInPermissions,
      grantHours: number,
    ) {
      const response = await post<object>(
        V2,
        `projects/${projectId}/check-ins`,
        checkInBody(permissions, grantHours),
      );
      return ProjectCheckIn.fromJson(response.data);
    },
    // Full replace; only affects visitors who check in afterwards.
    async updateCheckIn(checkInId: string, permissions: CheckInPermissions, grantHours: number) {
      const response = await put<object>(
        V2,
        `check-ins/${checkInId}`,
        checkInBody(permissions, grantHours),
      );
      return ProjectCheckIn.fromJson(response.data);
    },
    async listCheckInVisitors(checkInId: string) {
      const response = await get<object[]>(V2, `check-ins/${checkInId}/visitors`);
      return response.data.map((json) => CheckInVisitor.fromJson(json));
    },
    // Unlike the other endpoints this response is not wrapped in `data`.
    async getCheckInCode(checkInId: string) {
      const response = await getRaw(V2, `check-ins/${checkInId}/code`);
      if (!response.ok) {
        // 410 when the check-in has been closed.
        throw new Error(`Fetching the check-in code failed with status ${response.status}`);
      }
      return CheckInCode.fromJson(await response.json());
    },
    // Switches to a new code right away; the previous code stops working. Same response as getCheckInCode.
    async renewCheckInCode(checkInId: string) {
      const response = await postRawJson<object>(V2, `check-ins/${checkInId}/code`, {});
      if (!response.ok) {
        // 410 when the check-in has been closed.
        throw new Error(`Renewing the check-in code failed with status ${response.status}`);
      }
      return CheckInCode.fromJson(await response.json());
    },
    // A code for a printed QR that keeps working for `validHours` (at most 8). Same unwrapped response as getCheckInCode.
    async createCheckInPrintCode(checkInId: string, validHours: number) {
      const response = await postRawJson<object>(V2, `check-ins/${checkInId}/print-code`, {
        valid_hours: validHours,
      });
      if (!response.ok) {
        // 410 when the check-in has been closed.
        throw new Error(
          `Creating the printable check-in code failed with status ${response.status}`,
        );
      }
      return CheckInPrintCode.fromJson(await response.json());
    },
    async closeCheckIn(checkInId: string) {
      await del<void>(V2, `check-ins/${checkInId}`);
    },
    // Takes the project access of a user away completely, also what is left of an expired grant. Requires
    // manage_users on the project; an admin always may.
    async revokeProjectAccess(userId: string, projectId: string) {
      await del<void>(V2, `users/${userId}/project-access/${projectId}`);
    },

    // Checklists (v2)
    async listChecklists() {
      const response = await get<object[]>(V2, "checklists");
      return response.data.map((json) => Checklist.fromJson(json));
    },
    async getChecklist(checklistId: string) {
      const response = await get<object>(V2, `checklists/${checklistId}`);
      return Checklist.fromJson(response.data);
    },
    async addChecklist(materialType: string, liftingCategory: LiftingCategory | null, companyId: string | null) {
      const response = await post<object>(V2, "checklists", {
        material_type: materialType,
        lifting_category: liftingCategory,
        company_uuid: companyId,
      });
      return Checklist.fromJson(response.data);
    },
    // Basic — no content_blocks/actions editing UI exists yet, so a new card is always created empty
    // (both are required-but-may-be-empty arrays server-side); those get filled in later once the
    // content-block editor exists.
    async addChecklistCard(
      checklistId: string,
      name: TextContent,
      cardType: string,
      column: number,
      positionInColumn: number,
    ) {
      const response = await post<object>(V2, `checklists/${checklistId}/cards`, {
        name,
        card_type: cardType,
        content_blocks: [],
        actions: [],
        column,
        position_in_column: positionInColumn,
      });
      return FlowCard.fromJson(response.data);
    },
    // Full-replace PUT — resends every field the card already has (content_blocks/actions included,
    // even though there's no editor for those yet) so a move/reorder never silently drops them.
    async updateChecklistCard(checklistId: string, card: FlowCard) {
      const response = await put<object>(
        V2,
        `checklists/${checklistId}/cards/${card.uuid}`,
        FlowCard.toJson(card),
      );
      return FlowCard.fromJson(response.data);
    },
    async deleteChecklistCard(cardUuid: string) {
      await del(V2, `checklist-cards/${cardUuid}`);
    },
    // Uploads one image for one language variant of one IMAGE content block — the caller embeds the
    // returned {path, url} into that block's content[language] and saves the whole card afterwards
    // via updateChecklistCard(); this endpoint itself has no notion of which card/block it's for.
    async uploadChecklistCardImage(file: File): Promise<{ path: string; url: string }> {
      const response = await postFile<{ path: string; url: string }>(
        V2,
        "checklist-cards/images",
        "image",
        file,
      );
      const json = await response.json();
      return json.data;
    },

    // Lifting category assistant (v2): a single flow of cards, without a parent like a checklist.
    async listLiftingCategoryCards() {
      const response = await get<object[]>(V2, "lifting-category-cards");
      return response.data.map((json) => FlowCard.fromJson(json));
    },
    async addLiftingCategoryCard(
      name: TextContent,
      cardType: string,
      column: number,
      positionInColumn: number,
    ) {
      const response = await post<object>(V2, "lifting-category-cards", {
        name,
        card_type: cardType,
        content_blocks: [],
        actions: [],
        column,
        position_in_column: positionInColumn,
      });
      return FlowCard.fromJson(response.data);
    },
    // Full-replace PUT, like updateChecklistCard().
    async updateLiftingCategoryCard(card: FlowCard) {
      const response = await put<object>(
        V2,
        `lifting-category-cards/${card.uuid}`,
        FlowCard.toJson(card),
      );
      return FlowCard.fromJson(response.data);
    },
    async deleteLiftingCategoryCard(cardUuid: string) {
      await del(V2, `lifting-category-cards/${cardUuid}`);
    },
    // See uploadChecklistCardImage().
    async uploadLiftingCategoryCardImage(file: File): Promise<{ path: string; url: string }> {
      const response = await postFile<{ path: string; url: string }>(
        V2,
        "lifting-category-cards/images",
        "image",
        file,
      );
      const json = await response.json();
      return json.data;
    },
    // Companies
    async listCompanies() {
      const response = await get<object[]>(V1, "companies?withArchived=true");
      return response.data.map((json) => Company.fromJson(json));
    },
    // Projects
    async listProjects() {
      const response = await get<object[]>(V1, "projects?withArchived=true");
      return response.data.map((json) => Project.fromJson(json));
    },
    // QuestionSets
    async listQuestionSets() {
      const response = await get<object[]>(V1, "question_sets");
      return response.data.map((json) => QuestionSet.fromJson(json));
    },
    // Assessors
    async listAssessors() {
      const response = await get<object[]>(V1, "assessors");
      return response.data.map((json) => Assessor.fromJson(json));
    },
    // Users
    async listUsers() {
      const response = await get<object[]>(V1, "users");
      return response.data.map((json) => User.fromJson(json));
    },
    async addAssessor(
      firstName: string,
      lastName: string,
      company: string | null,
      email: string | null,
      phone: string | null,
    ) {
      await post<void>(V1, `assessors`, {
        first_name: firstName,
        last_name: lastName,
        company: company,
        email: email,
        phone: phone,
      });
    },
    async updateAssessor(assessor: Assessor, fields: Partial<Assessor>) {
      await put<void>(V1, `assessors/${assessor.id}`, {
        first_name: fields.first_name ?? assessor.first_name,
        last_name: fields.last_name ?? assessor.last_name,
        options: fields.options ? { ...assessor.options, ...fields.options } : assessor.options,
        company: fields.company === undefined ? assessor.company : fields.company,
        email: fields.email === undefined ? assessor.email : fields.email,
        phone: fields.phone === undefined ? assessor.phone : fields.phone,
      });
    },
    async destroyAssessor(assessorId: string) {
      await del<void>(V1, `assessors/${assessorId}`);
    },
    // Inspections
    async listInspections() {
      const response = await get<object[]>(V1, "inspections?withArchived=true");
      return response.data.map((json) => Inspection.fromJson(json));
    },
    async archiveInspection(inspectionId: string) {
      await del<void>(V1, `inspections/${inspectionId}`);
    },
    async revertInspection(inspectionId: string) {
      await post<void>(V1, `inspections/${inspectionId}/revert`, {});
    },
    async destroyInspection(inspectionId: string) {
      await del<void>(V1, `inspections/${inspectionId}/delete`);
    },
    async addInspection(companyId: string, projectId: string, questionSetId: string, name: string | null) {
      const response = await put<object>(V1, `inspections/${v4()}`, {
        company_id: companyId,
        project_id: projectId,
        question_set_id: questionSetId,
        name: name,
      });
      return Inspection.fromJson(response.data);
    },
    async editInspectionExtraFields(
      inspection: Inspection,
      fields: { [key: string]: string },
      loadCapacityFields: LoadCapacity[],
      liftingEquipments: LiftingEquipment[],
      inspectionNumber?: string,
    ) {
      const response = await put<object>(V1, `inspections/${inspection.id}`, {
        ...(inspectionNumber !== undefined ? { inspection_number: inspectionNumber } : {}),
        extra_fields: {
          ...inspection.extraFields,
          ...fields,
        },
        load_capacities: loadCapacityFields,
        lifting_equipments: liftingEquipments,
      });
      return Inspection.fromJson(response.data);
    },
    async generateAssessmentName(company: Company, questionSet: QuestionSet) {
      const response = (await post(V1, "generate-exam-name", {
        company: company.name,
        question_set_type: questionSet.type,
      })) as unknown as { name: string };
      return response.name;
    },
    async addOrUpdateInspection(inspectionId: string, data: object) {
      await put<void>(V1, `inspections/${inspectionId}`, data);
    },
    async listAvailableInspectionSeverities(inspectionId: string) {
      const response = await get<
        {
          question_id: string;
          question_number: string | null;
          available_severities: InspectionSeverity[];
        }[]
      >(V1, `inspections/${inspectionId}/findings/available-severities`);
      return response.data;
    },
    // Exams
    async listExams() {
      const response = await get<object[]>(V1, "exams?withArchived=true");
      return response.data.map((json) => Exam.fromJson(json));
    },
    async archiveExam(examId: string) {
      await del<void>(V1, `exams/${examId}`);
    },
    async destroyExam(examId: string) {
      await del<void>(V1, `exams/${examId}/delete`);
    },
    async revertExam(examId: string) {
      await post<void>(V1, `exams/${examId}/revert`, {});
    },
    async sendExamReport(
      examId: string,
      options: {
        emails: string[];
        includePdf: boolean;
        separateContractors: boolean;
        includeCsv: boolean;
        includeProjectCsv: boolean;
        includeCertificate: boolean;
        includeFullExport: boolean;
      },
    ) {
      // The api expects the flags as the strings "true" and "false".
      const flag = (value: boolean) => (value ? "true" : "false");
      await post<void>(V1, `exams/${examId}/send_reports`, {
        emails: options.emails,
        include_pdf: flag(options.includePdf),
        separate_contractors: flag(options.separateContractors),
        include_csv: flag(options.includeCsv),
        include_project_csv: flag(options.includeProjectCsv),
        include_certificate: flag(options.includeCertificate),
        include_full_export: flag(options.includeFullExport),
      });
    },
    async regenCertificatesOnExam(examId: string) {
      await post<void>(V1, `exams/${examId}/regen-certificates`, {});
    },
    async addOrUpdateExam(examId: string, data: object) {
      await put<void>(V1, `exams/${examId}`, data);
    },
    async moveParticipant(examId: string, participantId: string, newExamId: string) {
      await post<void>(V1, `exams/${examId}/participants/${participantId}/move`, {
        exam_id: newExamId,
      });
    },
    // ExamParticipants
    async updateParticipant(examId: string, participantId: string, data: object) {
      await put<void>(V1, `exams/${examId}/participants/${participantId}`, data);
    },
    async listExamParticipants(examId: string) {
      const response = await get<object[]>(V1, `exams/${examId}/participants`);
      return response.data.map((json) => ExamParticipant.fromJson(json));
    },
    async listAllParticipants(withScore = false) {
      const response = await get<object[]>(
        V1,
        `participants?withScore=${withScore ? "true" : "false"}`,
      );
      return response.data.map((json) => ExamParticipant.fromJson(json));
    },
    // Inspection answers
    async getInspectionAnswers(inspectionId: string) {
      const response = await getRaw(V1, `inspections/${inspectionId}/results`);
      return (await response.json()) as Record<string, QuestionAnswerValue>;
    },
    async updateInspectionAnswers(
      inspectionId: string,
      answers: Record<string, QuestionAnswerValue>,
    ) {
      await put<void>(V1, `inspections/${inspectionId}/results`, answers);
    },
    // Inspection findings
    async listFindings(inspectionId: string) {
      const response = await get<object[]>(V1, `inspections/${inspectionId}/findings`);
      return response.data.map((json) => InspectionFinding.fromJson(json));
    },
    async updateFinding(
      inspectionId: string,
      findingId: string,
      questionId: string,
      severity: InspectionSeverity,
      description: string,
      comments: InspectionComment[] | null,
    ) {
      await put<object[]>(V1, `inspections/${inspectionId}/findings/${findingId}`, {
        inspection_question_id: questionId,
        severity: severity,
        description: description,
        comments: comments?.map((c) => ({
          user: c.user,
          comment: c.comment,
          is_fixed_within_24_hours: c.isFixedWithin24Hours,
          is_fixed_at: c.isFixedAt,
        })),
      });
    },
    async deleteFinding(inspectionId: string, findingId: string) {
      await del(V1, `inspections/${inspectionId}/findings/${findingId}`);
    },
    // Shared inspection findings
    async listSharedInspectionFindings(inspectionId: string) {
      const response = await get<object[]>(V1, `inspections/${inspectionId}/shared-findings`);
      return response.data.map((json) => SharedInspectionFinding.fromJson(json));
    },
    async sendSharedInspectionFindings(
      inspectionId: string,
      recipients: { name: string; email: string }[],
    ) {
      await post(V1, `inspections/${inspectionId}/shared-findings/send`, { recipients });
    },
    async revokeSharedInspectionFinding(inspectionId: string, sharedInspectionId: string) {
      await post(
        V1,
        `inspections/${inspectionId}/shared-findings/${sharedInspectionId}/revoke`,
        {},
      );
    },

    async listPhotos(inspectionId: string) {
      const response = await get<object[]>(V1, `inspections/${inspectionId}/photos`);
      return response.data.map((json) => InspectionPhoto.fromJson(json));
    },
    async addPhotos(inspectionId: string, photos: File[]) {
      const formData = new FormData();
      photos.forEach((photo) => formData.append("images[]", photo));
      await postRawForm<void>(V1, `inspections/${inspectionId}/photos`, formData);
    },
    async updatePhoto(
      inspectionId: string,
      photoId: string,
      rotation: number,
      description: string,
      questionId?: string | null,
    ) {
      await put(V1, `inspections/${inspectionId}/photos/${photoId}`, {
        description,
        rotation,
        ...(questionId !== undefined ? { question_uuid: questionId } : {}),
      });
    },
    async updatePhotoOrder(inspectionId: string, photoIds: string[]) {
      await post(V1, `inspections/${inspectionId}/photos/reorder`, {
        photo_ids: photoIds,
      });
    },
    async deletePhoto(inspectionId: string, photoId: string) {
      await del(V1, `inspections/${inspectionId}/photos/${photoId}`);
    },

    async listAttachments(inspectionId: string) {
      const response = await get<object[]>(V1, `inspections/${inspectionId}/attachments`);
      return response.data.map((json) => InspectionAttachment.fromJson(json));
    },
    async addAttachments(inspectionId: string, attachments: File[]) {
      const formData = new FormData();
      attachments.forEach((attachment) => formData.append("files[]", attachment));
      await postRawForm<void>(V1, `inspections/${inspectionId}/attachments`, formData);
    },
    async updateAttachment(inspectionId: string, attachmentId: string, name: string) {
      await put(V1, `inspections/${inspectionId}/attachments/${attachmentId}`, {
        name,
      });
    },
    async updateAttachmentOrder(inspectionId: string, attachmentIds: string[]) {
      await post(V1, `inspections/${inspectionId}/attachments/reorder`, {
        attachment_ids: attachmentIds,
      });
    },
    async deleteAttachment(inspectionId: string, attachmentId: string) {
      await del(V1, `inspections/${inspectionId}/attachments/${attachmentId}`);
    },
    async updateInspectionContact(
      inspectionId: string,
      name: string,
      email: string,
      phone: string,
      extra: string,
    ) {
      await post<void>(V1, `inspections/${inspectionId}/contact`, {
        contact_name: name,
        contact_email: email,
        contact_phone: phone,
        contact_extra: extra,
      });
    },
    async updateInspectionStatus(inspectionId: string, status: string) {
      await put<void>(V1, `inspections/${inspectionId}/status`, {
        status: status,
      });
    },
    // Returns undefined when this inspection's question set has no remarks question configured.
    async getInspectionRemarks(inspectionId: string): Promise<string | null | undefined> {
      const response = await fetch(`${V1}/inspections/${inspectionId}/remarks`, {
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
      });
      if (response.status === 422) {
        return undefined;
      }
      await checkForValidationErrors(response);
      const json = (await response.json()) as { remarks: string | null };
      return json.remarks;
    },
    async updateInspectionRemarks(inspectionId: string, remarks: string | null) {
      const response = await fetch(`${V1}/inspections/${inspectionId}/remarks`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ remarks }),
      });
      if (response.status === 422) {
        const json = await response.json();
        if ("message" in json && "errors" in json && typeof json.errors === "object") {
          throw new ValidationError(json.message, json.errors);
        }
        const message = "error" in json ? json.error : "Opslaan van de opmerkingen is mislukt";
        throw new ValidationError(message, { remarks: [message] });
      }
      await checkForValidationErrors(response);
      const json = (await response.json()) as { remarks: string | null };
      return json.remarks;
    },
    getExamExportUrl(examId: string, raw: boolean) {
      return `${V1}/exams/${examId}/results/export?raw=${raw ? "true" : "false"}&access_token=${getToken()}`;
    },
    getInspectionReportUrl(inspectionId: string, quality?: string) {
      return (
        `${V1}/inspections/${inspectionId}/export?access_token=${getToken()}` +
        (quality !== undefined ? `&mode=${quality}` : "")
      );
    },
    getExamReportUrl(examId: string, contractor?: string) {
      return `${V1}/exams/${examId}/results/report?${contractor ? `contractor=${encodeURIComponent(contractor)}&` : ""}access_token=${getToken()}`;
    },
    getExportEmptyTemplateUrl() {
      return `${V1}/export/template_leeg.csv?access_token=${getToken()}`;
    },
    getExportConceptTemplateUrl() {
      return `${V1}/export/template_concept.csv?access_token=${getToken()}`;
    },
    async importCsvTest(content: string) {
      const response = await post<object[]>(V1, "import/test", { content });
      return response.data.map((json) => ProposedChange.fromJson(json));
    },
    async importCsvApply(content: string) {
      const response = await post<object[]>(V1, "import/apply", { content });
      return response.data.map((json) => ProposedChange.fromJson(json));
    },
    async exportSelectionExcelTestReport(
      examIds: string[],
      contractors: string[] | null,
      subContractors: string[] | null,
      participants: string[] | null,
    ) {
      const body: { [key: string]: any } = { exam_ids: examIds };
      if (contractors) body["contractors"] = contractors;
      if (subContractors) body["subcontractors"] = subContractors;
      if (participants) body["participants"] = participants;
      const response = await postRawJson<object[]>(V1, "export-selection/test-report-excel", body);
      return await response.blob();
    },
    async exportSelectionAdminDashboardPdf(
      examIds: string[],
      groupContractors: string[],
      contractors: string[] | null,
      subContractors: string[] | null,
      participants: string[] | null,
    ) {
      const body: { [key: string]: any } = {
        exam_ids: examIds,
        group_contractors: groupContractors,
      };
      if (contractors) body["contractors"] = contractors;
      if (subContractors) body["subcontractors"] = subContractors;
      if (participants) body["participants"] = participants;
      const response = await postRawJson<object[]>(V1, "export-selection/admin-dashboard", body);
      return await response.blob();
    },
    async exportSelectionAdminDashboardPdfPerContractor(
      examIds: string[],
      groupContractors: string[],
      contractors: string[] | null,
      subContractors: string[] | null,
      participants: string[] | null,
    ) {
      const body: { [key: string]: any } = {
        exam_ids: examIds,
        group_contractors: groupContractors,
      };
      if (contractors) body["contractors"] = contractors;
      if (subContractors) body["subcontractors"] = subContractors;
      if (participants) body["participants"] = participants;
      const response = await postRawJson<object[]>(
        V1,
        "export-selection/admin-dashboard-per-contractor",
        body,
      );
      return await response.blob();
    },
    async exportManagementDataReport(
      enabledPages: string[],
      coverPhoto: File | null,
      examIds: string[],
      groupContractors: string[],
      contractors: string[] | null,
      subContractors: string[] | null,
      participants: string[] | null,
    ) {
      const formData = new FormData();
      enabledPages.forEach((page) => formData.append("pages[]", page));
      if (coverPhoto) {
        formData.append("cover_image", coverPhoto);
      }
      examIds.forEach((examId) => formData.append("exam_ids[]", examId));
      groupContractors.forEach((groupContractor) =>
        formData.append("group_contractors[]", groupContractor),
      );
      if (contractors) {
        contractors.forEach((contractor) => formData.append("contractors[]", contractor));
      }
      if (subContractors) {
        subContractors.forEach((subcontractor) =>
          formData.append("subcontractors[]", subcontractor),
        );
      }
      if (participants) {
        participants.forEach((participant) => formData.append("participants[]", participant));
      }
      const response = await postRawForm<object[]>(
        V1,
        "export-selection/admin-data-report",
        formData,
      );
      return await response.blob();
    },
    async exportSelectionFullScorecardExcel(
      examIds: string[],
      contractors: string[] | null,
      subContractors: string[] | null,
      participants: string[] | null,
    ) {
      const body: { [key: string]: any } = { exam_ids: examIds };
      if (contractors) body["contractors"] = contractors;
      if (subContractors) body["subcontractors"] = subContractors;
      if (participants) body["participants"] = participants;
      const response = await postRawJson<object[]>(
        V1,
        "export-selection/full-scorecard-export",
        body,
      );
      return await response.blob();
    },
    async exportSelectionCertificatesPdf(
      examIds: string[],
      participants: string[] | null,
      includeFailed = false,
    ) {
      const body: { [key: string]: any } = { exam_ids: examIds };
      if (participants) body["participants"] = participants;
      if (includeFailed) body["include_failed"] = true;
      const response = await postRawJson<object[]>(V1, "certificates/download-bulk", body);
      return await response.blob();
    },
    async updateCompany(
      companyId: string,
      name: string,
      scoringMap?: CategoryScoringMap | null,
      alias?: string,
      colorPalette?: string,
      enabledQuestionSetIds?: string[],
    ) {
      await put<void>(V1, `companies/${companyId}`, {
        name,
        ...(scoringMap !== undefined
          ? {
              override_category_scores:
                scoringMap === null ? null : { scoring_per_score_category: scoringMap },
            }
          : {}),
        ...(alias !== undefined ? { alias: alias } : {}),
        ...(colorPalette !== undefined ? { color_palette: colorPalette } : {}),
        ...(enabledQuestionSetIds !== undefined
          ? { available_question_set_ids: enabledQuestionSetIds }
          : {}),
      });
    },
    async archiveCompany(companyId: string) {
      await del<void>(V1, `companies/${companyId}`);
    },
    async attachParentCompany(companyId: string, parentCompanyId: string) {
      await post<void>(V1, `companies/${companyId}/parent-companies/${parentCompanyId}`, {});
    },
    async detachParentCompany(companyId: string, parentCompanyId: string) {
      await del<void>(V1, `companies/${companyId}/parent-companies/${parentCompanyId}`);
    },
    async attachChildCompany(companyId: string, childCompanyId: string) {
      await post<void>(V1, `companies/${companyId}/child-companies/${childCompanyId}`, {});
    },
    async detachChildCompany(companyId: string, childCompanyId: string) {
      await del<void>(V1, `companies/${companyId}/child-companies/${childCompanyId}`);
    },
    async updateProject(
      companyId: string,
      projectId: string,
      name: string,
      scoringMap?: CategoryScoringMap | null,
    ) {
      await put<void>(V1, `companies/${companyId}/projects/${projectId}`, {
        name,
        ...(scoringMap !== undefined
          ? {
              override_category_scores:
                scoringMap === null ? null : { scoring_per_score_category: scoringMap },
            }
          : {}),
      });
    },
    async archiveProject(companyId: string, projectId: string) {
      await del<void>(V1, `companies/${companyId}/projects/${projectId}`);
    },
    async updateCompanyLogo(companyId: string, logo: File) {
      await postFile<void>(V1, `companies/${companyId}/logo`, "logo", logo);
    },
    async deleteCompanyLogo(companyId: string) {
      await del<void>(V1, `companies/${companyId}/logo`);
    },
    async updateProjectContact(
      companyId: string,
      projectId: string,
      name: string,
      email: string,
      phone: string,
      extra: string,
    ) {
      await post<void>(V1, `companies/${companyId}/projects/${projectId}/contact`, {
        contact_name: name,
        contact_email: email,
        contact_phone: phone,
        contact_extra: extra,
      });
    },
    async updateProjectLogo(companyId: string, projectId: string, logo: File) {
      await postFile<void>(V1, `companies/${companyId}/projects/${projectId}/logo`, "logo", logo);
    },
    async deleteProjectLogo(companyId: string, projectId: string) {
      await del<void>(V1, `companies/${companyId}/projects/${projectId}/logo`);
    },
    async listRoles() {
      const response = await get<object[]>(V1, `roles`);
      return response.data.map((json) => RealmRole.fromJson(json));
    },
    async addUserRole(userId: string, role: RealmRole) {
      await put<void>(V1, `users/${userId}/roles`, {
        role: role.id,
      });
    },
    async removeUserRole(userId: string, role: RealmRole) {
      await del<void>(V1, `users/${userId}/roles/${role.id}`);
    },
    async updateUserAttributes(
      userId: string,
      firstName: string,
      lastName: string,
      email: string,
      companies: string[],
      questionSets: string[],
      projects: string[],
    ) {
      await put<void>(V1, `users/${userId}/attributes`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        companies: companies,
        question_sets: questionSets,
        projects: projects,
      });
    },
    async deleteUser(userId: string) {
      await del<void>(V1, `users/${userId}`);
    },
    async manageUserLogin(userId: string, enabled: boolean) {
      await put<void>(V1, `users/${userId}`, {
        enabled: enabled,
      });
    },
    async addUser(
      firstName: string,
      lastName: string,
      email: string,
      username: string,
      password: string,
    ) {
      await post<void>(V1, `users`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        username: username,
        password: password,
      });
    },
    async updateUserPassword(userId: string, password: string) {
      await put<void>(V1, `users/${userId}`, {
        password,
      });
    },
    async updateContractor(companyId: string, name: string, oldName: string) {
      await put<void>(V1, `companies/${companyId}/rename-contractor`, {
        name,
        oldName,
      });
    },
    async updateSubContractor(companyId: string, name: string, oldName: string) {
      await put<void>(V1, `companies/${companyId}/rename-subcontractor`, {
        name,
        oldName,
      });
    },
    async listCertificates() {
      const response = await get<object[]>(V1, `certificates`);
      return response.data.map((json) => Certificate.fromJson(json));
    },
    async downloadCertificateUrl(certificateId: string) {
      return `${V1}/certificates/${certificateId}/download?access_token=${getToken()}`;
    },
    async addCompany(companyName: string) {
      await post<void>(V1, `companies`, {
        name: companyName,
      });
    },
    async addProject(companyId: string, projectName: string) {
      await post<void>(V1, `companies/${companyId}/projects`, {
        company_uuid: companyId,
        name: projectName,
      });
    },
    async listWhitelistedEmails() {
      const response = await get<object[]>(V1, `import-whitelist`);
      return response.data.map((json) => WhitelistedEmail.fromJson(json));
    },
    async addWhitelistedEmail(email: string) {
      await post<void>(V1, `import-whitelist`, {
        email: email,
      });
    },
    async deleteWhitelistedEmail(whitelistedEmailId: string) {
      await del<void>(V1, `import-whitelist/${whitelistedEmailId}`);
    },
    async listCertificateTypes() {
      const response = await get<object[]>(V1, `certificate-types`);
      return response.data.map((json) => CertificateType.fromJson(json));
    },
    async addCertificateType(name: string, questionSetId: string, isActive: boolean) {
      await post<void>(V1, `certificate-types`, {
        name: name,
        question_set_id: questionSetId,
        is_active: isActive,
      });
    },
    async editCertificateType(
      certificateTypeId: string,
      name: string,
      questionSetId: string,
      isActive: boolean,
    ) {
      await put<void>(V1, `certificate-types/${certificateTypeId}`, {
        name: name,
        question_set_id: questionSetId,
        isActive: isActive,
      });
    },
    async deleteCertificateType(certificateTypeId: string) {
      await del<void>(V1, `certificate-types/${certificateTypeId}`);
    },
    async archiveParticipant(examId: string, participantId: string) {
      await del<void>(V1, `exams/${examId}/participants/${participantId}?force=true`);
    },
    async unarchiveParticipant(examId: string, participantId: string) {
      await put<void>(V1, `exams/${examId}/participants/${participantId}/unarchive`, {});
    },
    async addParticipant(examId: string, data: object) {
      await post<void>(V1, `exams/${examId}/participants`, data);
    },
    async destroyParticipant(examId: string, participantId: string) {
      await del<void>(V1, `exams/${examId}/participants/${participantId}/delete`);
    },
    async bulkRenameContractor(participants: string[], name: string) {
      await put<void>(V1, "participants", {
        participants: participants,
        contractor_name: name,
      });
    },
    async bulkRenameSubcontractor(participants: string[], name: string) {
      await put<void>(V1, "participants", {
        participants: participants,
        subcontractor_name: name,
      });
    },

    async listUserPermissionsForCompany(companyId: string) {
      const response = await get<object[]>(V1, `companies/${companyId}/company-access`);
      return response.data.map((json) => UserPermissions.fromJson(json));
    },

    async listCompanyPermissionsForUser(userId: string) {
      const response = await get<object[]>(V1, `users/${userId}/company-access`);
      return response.data.map((json) => UserPermissions.fromJson(json));
    },

    // The caller's own permissions per company they can see (with company access, or read-only through
    // a project grant), as decided by the gateway's CompanyPolicy. Only uuid + permissions are kept: the
    // company data itself still comes from v1 `listCompanies`, which also includes archived companies.
    // Admin bypass is a realm-role concern, checked client-side (see usePermissions), not inferred from this.
    async getMyCompanyPermissions() {
      const response = await get<object[]>(V2, "companies");
      return response.data.map((json) => MyCompanyPermissions.fromJson(json));
    },

    async grantCompanyAccessToUser(
      userId: string,
      companyId: string,
      permissions: UserPermissionsSettings,
    ) {
      await put<void>(V1, `users/${userId}/company-access/${companyId}`, {
        can_execute_assessments: permissions.canExecuteAssessments,
        can_export_assessments: permissions.canExportAssessments,
        can_access_all_assessments: permissions.canAccessAllAssessments,
        can_access_assessment_history: permissions.canAccessAssessmentHistory,
        can_manage_companies: permissions.canManageCompanies,
        can_manage_companies_destructive: permissions.canManageCompaniesDestructive,
        can_manage_projects: permissions.canManageProjects,
        can_manage_projects_destructive: permissions.canManageProjectsDestructive,
        can_manage_assessments: permissions.canManageAssessments,
        can_manage_assessments_destructive: permissions.canManageAssessmentsDestructive,
        can_access_certificates: permissions.canAccessCertificates,
        can_access_dashboards: permissions.canAccessDashboards,
        can_access_web: permissions.canAccessWeb,
        can_access_mobile: permissions.canAccessMobile,
        can_import: permissions.canImport,
        can_manage_users: permissions.canManageUsers,
        can_manage_checklists: permissions.canManageChecklists,
        accessible_question_set_types: permissions.accessibleQuestionSetTypes,
      });
    },

    async revokeCompanyAccessFromUser(userId: string, companyId: string) {
      await del<void>(V1, `users/${userId}/company-access/${companyId}`);
    },
    async listDefaultInspectionAttachments() {
      const response = await get<object[]>(V1, `default-inspection-attachments`);
      return response.data.map((item) => DefaultInspectionAttachment.fromJson(item));
    },
    async uploadDefaultInspectionAttachment(
      file: File,
      name: string,
      order: number,
      fromDate: Date,
      toDate: Date,
    ) {
      const formData = new FormData();
      const renamedFile = new File([file], name, {
        type: file.type,
      });

      formData.append("file", renamedFile);
      formData.append("order", order.toString());
      formData.append("from_date", moment(fromDate).format("YYYY-MM-DD HH:mm"));
      formData.append("to_date", moment(toDate).format("YYYY-MM-DD HH:mm"));

      await postRawForm<void>(V1, `default-inspection-attachments`, formData);
    },
    async deleteDefaultInspectionAttachment(attachmentId: string) {
      await del<void>(V1, `default-inspection-attachments/${attachmentId}`);
    },
    async editDefaultInspectionAttachment(
      attachmentId: string,
      name: string,
      order: number,
      fromDate: Date,
      toDate: Date,
    ) {
      await put<void>(V1, `default-inspection-attachments/${attachmentId}`, {
        name: name,
        order: order,
        from_date: fromDate,
        to_date: toDate,
      });
    },
  };
};

export class ValidationError extends Error {
  constructor(
    public readonly message: string,
    public readonly errors: { [key: string]: string[] },
  ) {
    super(message);
  }
}
